import { UMEEE_TENANT_ID } from "@/utils/const";
import { isSystemRoleAtLeast } from "@shared-domain/unizo/permissions";
import { removeLocalStorageItem } from "@shared-ui/utils/localStorage";
import {
  clearCache as clearPreferenceCache,
  clearLegacyPreferences,
} from "@/lib/preferences/storage";
import type {
  WhoamiMe,
  WhoamiMembership,
} from "@shared-domain/auth-service/contract";

/**
 * PRODUCT_DEV-631: ログアウトせずにテナントを切り替える導線の判定ロジック。
 *
 * テナントは JWT に含まれず `Users.currentTenantId` (DDB) + リクエストの tenantId で
 * 解決されるため、切替は `PATCH /auth/me` + フルリロードで足りる。
 *
 * ## 認可モデル（重要）
 *
 * ここでの判定は「誰に導線を見せるか」という **UI ゲート**であり、認可の代替ではない。
 * サーバ側 (`auth-service/lambda/auth/me.ts`) は `authMiddleware()` を needsSysadminRole
 * 無しで適用しているため、**sysadmin でない一般ユーザーが直接 PATCH /auth/me を叩けば
 * 切替自体は成功する**。これは意図的で、Portal のログインフローも同じ API で
 * カレントテナントを設定するため、ここに sysadmin 必須のゲートを足すと全ユーザーの
 * ログインが壊れる。
 *
 * 実害が無いのは、切替先が「自分が所属し、かつ Unizo 契約済みのテナント」に限られることを
 * `ensureUserTenant` / `ensureTenantProduct` が保証しているため（＝権限昇格もテナント越境も
 * 起きない）。本フラグ判定はあくまで一般ユーザーに不要な導線を見せないためのもの。
 */

/**
 * テナント切替時に破棄する、テナントスコープの localStorage キー。
 *
 * フルリロードだけでは localStorage は消えず、前テナントの顧客名・企業名を含む検索履歴や、
 * 前テナントでしか解決できない presetId が次のテナントに持ち越される
 * （presetId の越境残留は PRODUCT_DEV-610 で実際に不具合化した既知パターン）。
 *
 * ⚠️ キー文字列の正本は各モジュール側にあるため、下記を変更するときは参照元も追うこと。
 *
 * PRODUCT_DEV-699: ここに残るのは**個人設定に載せなかったもの**だけになった。
 * 個人設定（プリセット・記録モード・通知バッジ・検索履歴ほか9キー）はサーバへ移り、
 * 端末側は下書きになったので、破棄は `clearPreferenceCache()` が受け持つ。
 */
export const TENANT_SCOPED_LOCAL_STORAGE_KEYS = [
  "prompt-template-active-id", // utils/promptStorage.ts
  "prompt-template-active-content", // utils/promptStorage.ts
] as const;

/**
 * テナントスコープの端末ローカル状態を破棄する（切替確定時に呼ぶ）。
 *
 * **個人設定の下書きは「端末の好みだから残す」ではなく全部捨てる。** サーバ側の
 * 保存先はテナントごとに分かれている（PK に `TENANT#` を含む）ので、下書きだけが
 * テナントを跨ぐと切替直後に前テナントの値が一瞬出る。旧キーも一緒に捨てる——
 * 残すと、切替先で設定が空のときに前テナントの旧キーを吸い上げてしまう。
 */
export function clearTenantScopedLocalState(): void {
  for (const key of TENANT_SCOPED_LOCAL_STORAGE_KEYS) {
    removeLocalStorageItem(key);
  }
  clearPreferenceCache();
  clearLegacyPreferences();
}

/**
 * テナント切替導線を出してよいユーザーか。
 *
 * 条件: Umeee テナントに所属し、その Umeee テナントで role=admin かつ specialFlags に
 * sysadmin を持つこと。
 *
 * ⚠️ `utils/role.ts` の checkCan などは「現在のテナント」基準のため
 * ここでは使わない。現在テナント基準にすると、他テナントへ切り替えた瞬間に導線が消えて
 * Umeee へ戻れなくなる（＝ログアウトが必要になり本課題の目的を果たさない）。
 */
export function canSwitchTenant(auth: WhoamiMe | undefined | null): boolean {
  const umeee = auth?.memberships?.find((m) => m.tenantId === UMEEE_TENANT_ID);
  if (!umeee) return false;
  return isSystemRoleAtLeast(umeee, "sysadmin");
}

/** テナント名ソート用（比較のたびに Collator を解決させない） */
const TENANT_NAME_COLLATOR = new Intl.Collator("ja");

/**
 * 切替先候補の一覧（テナント名の昇順）。
 *
 * whoami は productId=unizo で products を絞り込んで返すため、`products.length > 0` が
 * 「そのテナントで Unizo が使える」と同義になる。Unizo 未契約テナントを出しても
 * ensureTenantProduct で必ず 403 になるだけなので候補から除外する。
 */
export function listSwitchableTenants(
  auth: WhoamiMe | undefined | null,
): WhoamiMembership[] {
  // filter() が新配列を返すので元の memberships は破壊しない
  return (auth?.memberships ?? [])
    .filter((m) => (m.products?.length ?? 0) > 0)
    .sort((a, b) => TENANT_NAME_COLLATOR.compare(a.tenantName, b.tenantName));
}

/**
 * 切替先が MFA 必須なのに、現在のテナントが MFA 不要なケースだけ true。
 *
 * MFA 必須テナントへの入場時の TOTP 設定強制は Portal のログインフローが担っている
 * (useLoginHandlers.handleTenantChoice)。アプリ内切替でそこを迂回させないため、この
 * ケースだけは Portal 再ログインへ送る。現在テナントが既に MFA 必須なら、その
 * セッションは TOTP 検証済みなので通常の切替でよい。
 *
 * ⚠️ Portal の強制判定は Cognito のユーザー単位 TOTP 設定有無 (checkMFAPreference) で
 * 行うため、この条件は安全側に倒した近似になっている。既に TOTP 設定済みのユーザーでも
 * 「MFA 不要テナント → MFA 必須テナント」の切替では毎回再ログインになる。摩擦を減らすには
 * whoami 側に TOTP 設定済みフラグが要るので、必要になったら別課題で扱う。
 */
export function needsReloginForMfa(
  auth: WhoamiMe | undefined | null,
  target: WhoamiMembership,
): boolean {
  if (!target.mfaRequired) return false;
  const current = auth?.memberships?.find(
    (m) => m.tenantId === auth?.currentTenantId,
  );
  return !current?.mfaRequired;
}
